/**
 * Pure front-door decision for the Club tab (Plan A).
 *
 * Input is the native getClubStatus answer (or null before/without a shell).
 * Output is exactly what the template renders:
 *  - `banner`: the iCloud problem line (native-authored, rendered VERBATIM so
 *    there is ONE copy source — CloudAccountGuard on the Swift side), or null.
 *  - `shouldPromptForName`: the one-time "What's your name?" card. Only when
 *    the user is actually IN a club (leader or member) and unnamed — a role of
 *    "none" has no roster to be named in, and "unknown" (offline) must not
 *    prompt for a name that can't save.
 *  - `showSettingsButton`: UNCONDITIONALLY true (Codex review finding 1 — the
 *    OLD version tied this to status presence, so any getClubStatus failure
 *    silently hid the ONLY Release opt-in/invite path). The real, and ONLY,
 *    gate is native-method support: ClubTab.svelte ANDs this with
 *    `$supportsClubSettings`, so a plain browser with no shell still renders
 *    nothing, while a real shell mid-load or with a failed status still shows
 *    the button — its own sheet can always explain the account/role state.
 */
import type { ClubStatusResponse } from "@contract/types";

export interface ClubFrontDoor {
  banner: string | null;
  shouldPromptForName: boolean;
  showSettingsButton: boolean;
}

const FALLBACK_BANNER =
  "Couldn't check your iCloud account just now. Try again in a moment.";

export function clubFrontDoor(status: ClubStatusResponse | null): ClubFrontDoor {
  if (!status) {
    return { banner: null, shouldPromptForName: false, showSettingsButton: true };
  }
  if (!status.iCloudOk) {
    return {
      banner: status.accountMessage ?? FALLBACK_BANNER,
      shouldPromptForName: false,
      showSettingsButton: true,
    };
  }
  const inClub = status.role === "leader" || status.role === "member";
  const unnamed = status.myName == null || status.myName === "";
  return {
    banner: null,
    shouldPromptForName: inClub && unnamed,
    showSettingsButton: true,
  };
}

/**
 * The pure decision `saveName()` makes from a `setClubName` result (Codex review finding
 * 7b). No component-testing infra exists in this repo (no @testing-library/svelte, no DOM
 * mount tests anywhere in web/src) — this function IS the tested seam: it's the exact logic
 * `saveName()` calls, and composing it with `clubFrontDoor()` (see the tests above) proves
 * the real data flow end to end (save ok → clubStatus.myName set → shouldPromptForName
 * flips false → the card's `{#if}` hides it) without needing to mount the component.
 */
export interface NameSaveOutcome {
  promptShouldStayVisible: boolean;
  failed: boolean;
}

export function nameSaveOutcome(result: { ok: boolean; name?: string | null }): NameSaveOutcome {
  return result.ok
    ? { promptShouldStayVisible: false, failed: false }
    : { promptShouldStayVisible: true, failed: true };
}

// ---------------------------------------------------------------------------
// Item 2a (spec 2026-09-01 §2): ONE honest front door. The Club page renders by
// exactly one of these states; the three empty states each carry exactly one
// primary action and never the old "Pick a swimmer" card.
// ---------------------------------------------------------------------------

export type ClubFrontDoorState =
  | "noClub"
  | "leaderWaiting"
  | "memberNoSharedSwim"
  | "sharedMorning"
  | "iCloudBlocked"
  | "statusFailed";

export interface FrontDoorStateInput {
  role: ClubStatusResponse["role"];
  iCloudOk: boolean;
  statusLoadFailed: boolean;
  /** Clubmates the local store knows about (listCompanions), excluding you. */
  memberCount: number;
  /** The pager's currently shown morning has ≥2 swimmers with swims. */
  hasSharedSessionForSelectedDay: boolean;
  /** ANY morning in the club has ≥2 swimmers — once true the page keeps the
   *  sharedMorning layout while paging, so a "just you" day doesn't flip the
   *  whole screen back to the first-day card. */
  hasAnySharedSession: boolean;
}

export interface ClubBootingInput {
  /** The body has rendered once since the tab mounted. */
  shown: boolean;
  companionsLoading: boolean;
  /** A shell that has getClubStatus has not answered it yet. */
  statusPending: boolean;
  sessionsLoading: boolean;
  /** Essential state must arrive even if an earlier no-club screen was shown. */
  snapshotPending?: boolean;
}

/** Build 15b ("it kinda seems to refresh pretty continuously"): "Loading your club…" holds the body
 *  for the FIRST load only. Native fires `swimset:club-status-refresh` on every roster bump and every
 *  foreground, and the tab re-fetches companions and club status on it; gating the body on those
 *  re-fetches unmounted the strip and roster until the bridge answered, every time. Once the club has
 *  shown, it stays up while fresh answers are on the way. */
export function clubBooting(i: ClubBootingInput): boolean {
  return !!i.snapshotPending || (!i.shown && (i.companionsLoading || i.statusPending || i.sessionsLoading));
}

export function resolveFrontDoorState(i: FrontDoorStateInput): ClubFrontDoorState {
  if (i.statusLoadFailed) return "statusFailed";
  if (!i.iCloudOk) return "iCloudBlocked";
  if (i.role === "none") return "noClub";
  // "unknown" = offline / no shell: whatever is on disk decides. No members on
  // disk and no account answer reads as no club — the sheet can always correct it.
  if (i.role === "unknown" && i.memberCount === 0) return "noClub";
  if (i.hasSharedSessionForSelectedDay || i.hasAnySharedSession) return "sharedMorning";
  if (i.role === "leader" && i.memberCount === 0) return "leaderWaiting";
  return "memberNoSharedSwim";
}

/** Header subtitle under "Club". The bridge exposes no club name, so this is the
 *  headcount only (mockups: "· just you so far" / "· N swimmers"). */
export function clubSubtitle(state: ClubFrontDoorState, memberCount: number): string | null {
  switch (state) {
    case "leaderWaiting":
      return "Just you so far";
    case "memberNoSharedSwim":
    case "sharedMorning":
      return `${memberCount + 1} swimmers`;
    default:
      return null;
  }
}

/** The Club tab's eyebrow under the date row (B-031, build 14): the retired "Club" title and its
 *  subtitle, in one small line ("Club · 2 swimmers"). "Club" alone when there is no headcount. */
export function clubEyebrow(state: ClubFrontDoorState, memberCount: number): string {
  const sub = clubSubtitle(state, memberCount);
  return sub ? `Club · ${sub}` : "Club";
}

/** The bridge's CompanionSummary carries no role. The one sound inference: a
 *  MEMBER who sees exactly one clubmate is looking at the leader (every club has
 *  one, and a member's list is the leader's zone). Anything else is unknown. */
export function inferLeaderName(
  role: ClubStatusResponse["role"],
  companions: ReadonlyArray<{ swimmerLabel: string }>,
): string | null {
  if (role === "member" && companions.length === 1) return companions[0].swimmerLabel;
  return null;
}

/** "Who's in" roster meta line. */
export function swimCountLine(count: number, isYou = false): string {
  if (count === 0) return isYou ? "Your next swim shows up here" : "No swims yet";
  return `${count} ${count === 1 ? "swim" : "swims"} in the club`;
}

/** Partial snapshot content does not establish an all-time HealthKit count. */
export function ownHistoryLine(loading: boolean, failed: boolean, count: number): { line: string; complete: boolean } {
  if (loading) return { line: "Loading your swim history…", complete: false };
  if (failed) return { line: "Swim history unavailable", complete: false };
  return { line: swimCountLine(count, true), complete: true };
}
